import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { ActivityWishlistService } from '../activity-wishlist.service';
import { ActivityWishlist } from '../activity-wishlist';

@Component({
    selector: 'app-activity-wishlist',
    templateUrl: './activity-wishlist.component.html',
    styleUrls: ['./activity-wishlist.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class ActivityWishlistComponent implements OnInit {

    wishlist: ActivityWishlist = new ActivityWishlist();
    wishlistId: number;
    loading = true;
    saving = false;
    sending = false;
    deleting = false;
    parsedItems: any[] = [];
    statuses: any[];

    constructor(
        private wishlistService: ActivityWishlistService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        this.statuses = wishlistService.statuses;
    }

    ngOnInit(): void {
        this.wishlistId = Number(this.route.snapshot.paramMap.get('wishlistId'));
        if (this.wishlistId) {
            this.loadWishlist();
        }
    }

    loadWishlist() {
        this.loading = true;
        this.wishlistService.findById(this.wishlistId).pipe(first()).subscribe(
            (res: any) => {
                this.wishlist = res.data.activityWishlist || res.data.wishlist || res.data;
                this.parseItems();
            },
            (err: any) => {
                console.error('load wishlist error:', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load wishlist.', life: 4000 });
            },
            () => { this.loading = false; }
        );
    }

    parseItems() {
        try {
            if (this.wishlist.itemsJson) {
                this.parsedItems = JSON.parse(this.wishlist.itemsJson);
            } else {
                this.parsedItems = [];
            }
        } catch (e) {
            console.warn('Could not parse itemsJson:', e);
            this.parsedItems = [];
        }
    }

    onSave() {
        this.saving = true;
        this.wishlistService.update(this.wishlist).pipe(first()).subscribe(
            (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Wishlist updated.', life: 3000 });
                this.wishlist = res.data.activityWishlist || res.data.wishlist || this.wishlist;
                this.parseItems();
            },
            (err: any) => {
                console.error('save error:', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not save.', life: 4000 });
            },
            () => { this.saving = false; }
        );
    }

    onSendToGuest() {
        this.sending = true;
        this.wishlistService.sendSuggestion(this.wishlistId).pipe(first()).subscribe(
            (res: any) => {
                this.messageService.add({ severity: 'success', summary: 'Sent', detail: 'Suggestion sent!', life: 3000 });
                this.loadWishlist();
            },
            (err: any) => {
                console.error('send error:', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not send suggestion.', life: 4000 });
            },
            () => { this.sending = false; }
        );
    }

    onDelete() {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete this wish list?',
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.deleting = true;
                this.wishlistService.delete(this.wishlistId).pipe(first()).subscribe(
                    () => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Wishlist deleted.', life: 2000 });
                        setTimeout(() => this.router.navigate(['activity-wishlists']), 1500);
                    },
                    (err: any) => {
                        console.error('delete error:', err);
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not delete.', life: 4000 });
                        this.deleting = false;
                    }
                );
            }
        });
    }

    canSendToGuest(): boolean {
        return this.wishlist && (this.wishlist.status === 'submitted' || this.wishlist.status === 'suggested');
    }

    getStatusSeverity(status: string): string {
        switch (status) {
            case 'submitted':  return 'warning';
            case 'suggested':  return 'info';
            case 'viewed':     return 'secondary';
            case 'confirmed':  return 'success';
            case 'processing': return 'help';
            case 'completed':  return 'secondary';
            default:           return 'secondary';
        }
    }

    goBack() {
        this.router.navigate(['activity-wishlists']);
    }
}
